import { fetchListAllTransactionList } from "@/api/merchant/request.get";
import { numberSeparator, toPersianNumber } from "@/hooks/useThousandsSeparator";
import { DetailIcon } from "@/shared/components/common/icons";
import CustomTable from "@/shared/components/common/table";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import { CircularProgress, Grid} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import moment from "jalali-moment";
import StackKit from "@/shared/components/kit/stack";
import { MerchantTransaction, MerchantTransactionResponse } from "@/types/api/merchant/merchant.type";
import { useNavigate } from "react-router";

/**
 * DeviceTransactionDetails
 *
 * این کامپوننت جزئیات تمام تراکنش‌های مربوط به دستگاه را بازیابی، پردازش و در یک جدول قابل صفحه‌بندی نمایش می‌دهد.
 *
 * قابلیت‌ها:
 * - دریافت لیست تراکنش‌ها از سرور با استفاده از React Query و پشتیبانی از مدیریت وضعیت بارگذاری.
 * - صفحه‌بندی داینامیک بر اساس state داخلی کامپوننت.
 * - امکان جست‌وجو در تراکنش‌ها از طریق پارامتر `searchText`.
 * - تبدیل و فرمت‌کردن داده‌های تراکنش شامل:
 *   - شماره ردیف به صورت فارسی.
 *   - جداکننده هزارگان برای مبلغ.
 *   - تبدیل timestamp به تاریخ و ساعت شمسی با استفاده از moment-jalali.
 *   - نمایش شماره کارت مبدا و مقصد با جهت چپ‌به‌راست در صورت وجود.
 *   - تبدیل نوع تراکنش به معادل فارسی با استفاده از نگاشت داخلی.
 * - نمایش جدول تراکنش‌ها همراه با مجموع داده‌ها و کنترل صفحه‌بندی.
 * - نمایش لایه بارگذاری شفاف زمانی که داده‌ها در حال fetch هستند.
 *
 * @component
 * @returns JSX.Element کامپوننت جدول تراکنش‌ها با قابلیت بارگذاری، صفحه‌بندی و فرمت داده
 */

const DeviceTransactionDetails = () => {
  const navigation = useNavigate();
  const [pages, setPages] = useState<number>(1);
  const [searchText, setSearchText] = useState<string>("");

  const dataHeader = [
    { id: "rowId", title: "ردیف" },
    { id: "amount", title: "مبلغ (ریال)" },
    { id: "ts", title: "ساعت و تاریخ" },
    { id: "ref_type", title: "نوع تراکنش" },
    { id: "source_card_number", title: "مبدا تراکنش" },
    { id: "destination_card_number", title: "مقصد تراکنش" },
    { id: "ref_id", title: "کد رهگیری تراکنش" },
    { id: "test3", title: "توضیحات" },
  ];

  // ✅ UseQuery — React Query v5 compatible
  const {data: dataListChart,isPending,isFetching,} = useQuery<MerchantTransactionResponse, Error>({
    queryKey: ["fetchListAll-dataTransAction", pages, searchText],
    queryFn: async (): Promise<MerchantTransactionResponse> => {
      const res = await fetchListAllTransactionList(pages, searchText, 10);
      return res;
    },
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev, 
  });

  
  // ✅ Safely map data
  const dataNew =
    dataListChart?.all_merchant_transactions?.map(
      (item: MerchantTransaction, index: number) => {
        const refTypeMap: Record<string, string> = {
          transfer: "انتقال",
          order: "سفارش",
          "pro-ref-earn": "کارمزد چند سطحی حرفه‌ای",
          "ref-earn": "کارمزد تک سطحی",
          withdraw: "برداشت",
          installments: "اقساط",
          deposite: "افزایش موجودی",
          creditScoreFee: "کارمزد اعتبارسنجی",
          resellerOrder: "-",
          bistCard: "20 کارت",
          edit: "ویرایش",
          "pos-pazirande": "دستگاه پوز",
          "pos-ghishe": "-",
          wallet: "کیف پول",
          ipg: "Ipg",
          "edit-pazirande": "اصلاح توسط پذیرنده",
          gift_fee: "-",
          emv: "خرید emv",
          qr_code: "QR Code",
          loam: "وام",
        };

        return {
          ...item,
          rowId: toPersianNumber(index + 1),
          amount: numberSeparator(item.amount),
          ts: toPersianNumber(
            `${moment.unix(item.ts).locale("fa").format("HH:mm")} ${moment
              .unix(item.ts)
              .locale("fa")
              .format("jYYYY/jMM/jDD")}`
          ),
          source_card_number: item.source_card_number ? (
            <TypoGraphyKit variant="body1" sx={{ direction: "ltr" }}>
              {toPersianNumber(item.source_card_number)}
            </TypoGraphyKit>
          ) : (
            "_"
          ),
          destination_card_number: item.destination_card_number ? (
            <TypoGraphyKit variant="body1" sx={{ direction: "ltr" }}>
              {toPersianNumber(item.destination_card_number)}
            </TypoGraphyKit>
          ) : (
            "_"
          ),
          ref_type: refTypeMap[item.ref_type] || item.ref_type,
          ref_id: toPersianNumber(item.ref_id),
        };
      }
    ) ?? [];

  return (
    <Grid container sx={{ padding: "10px", width: "100%" }}>
      <Grid size={12} sx={{ display: "flex", justifyContent: "start", gap: 2}} >
        <DetailIcon />
        <TypoGraphyKit className="cursor-pointer" onClick={() => navigation("/wallet-financial-reports")} variant="h6">جزئیات تراکنش دستگاه</TypoGraphyKit>
      </Grid>
      <Grid size={12} sx={{ padding: "10px", position: "relative",overflowX:'auto' }}>
        <CustomTable dataHeader={dataHeader} dataTbody={dataNew} total={dataListChart?.meta?.total || 0} setPages={setPages} currentPage={pages}/>
        {(isPending || isFetching) && (
          <StackKit
            sx={{position:'absolute',top:0,down:0,left:0,right:0,height: "100%",width: "100%",borderRadius:'1rem',background:"linear-gradient(309.62deg, rgba(26, 71, 60, 0.13) -0.33%, rgba(255, 255, 255, 0.13) 78.26%)",}} 
            justifyContent={"center"} alignItems={"center"}>
            <CircularProgress sx={{ color: "#fff" }} size={24} />
          </StackKit>
        )}
      </Grid>
    </Grid>);
};

export default DeviceTransactionDetails;
